import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StatementsCard from "@/components/StatementsCard";
import { IncomingCallModal, ActiveCallModal } from "@/components/interpreter/CallModals";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { acquireSignaling, releaseSignaling } from "@/lib/signaling";
import { startVoiceCall } from "@/lib/call";
import { startRingtone } from "@/lib/ringtone";

const STATUS_COLOR = {
  ringing: "bg-amber-100 text-amber-700 border-amber-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
  missed: "bg-rose-100 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function InterpreterPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [online, setOnline] = useState(user?.online || false);
  const [incoming, setIncoming] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [calls, setCalls] = useState([]);
  const [billing, setBilling] = useState({ total_minutes: 0, total_calls: 0, total_payout: 0 });
  const [stats, setStats] = useState({ today_calls: 0, today_minutes: 0, today_pay: 0 });
  const [callStatus, setCallStatus] = useState("");
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const audioRef = useRef(null);
  const rtcRef = useRef(null);
  const signalingRef = useRef(null);

  useEffect(() => {
    signalingRef.current = acquireSignaling();
    return () => releaseSignaling();
  }, []);
  useEffect(() => { setOnline(!!user?.online); }, [user?.online]);

  const loadHistory = useCallback(async () => {
    try {
      const [c, b, s] = await Promise.all([
        api.get("/calls/mine/list"), api.get("/billing/mine"), api.get("/interpreter/stats"),
      ]);
      setCalls(c.data);
      setBilling(b.data);
      setStats(s.data);
    } catch (e) {
      console.warn("[interpreter] load failed", e);
    }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Poll for incoming calls while online and not in a call
  useEffect(() => {
    if (!online || activeCall?.id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/calls/incoming");
        setIncoming((prev) => (data ? (prev?.id === data.id ? prev : data) : null));
      } catch (e) {
        console.debug("[interpreter] incoming poll failed", e);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [online, activeCall?.id]);

  // Ring + desktop notification for incoming calls
  useEffect(() => {
    if (!incoming || activeCall) return;
    const stop = startRingtone();
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("Incoming interpretation call", {
          body: `${incoming.customer_company || incoming.customer_name} · ${incoming.language}`,
        });
      } catch (e) {
        console.debug("[interpreter] notification failed", e);
      }
    }
    return stop;
  }, [incoming, activeCall]);

  const cleanupCall = useCallback((notify = true) => {
    try { rtcRef.current?.close(); } catch (e) { console.warn("[interpreter] rtc close failed", e); }
    rtcRef.current = null;
    setActiveCall(null);
    setCallStatus("");
    setMuted(false);
    if (notify) toast("Call ended");
  }, []);

  // Poll active call status
  useEffect(() => {
    if (!activeCall?.id) return;
    const callId = activeCall.id;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/calls/${callId}`);
        setActiveCall(data);
        if (["ended", "missed", "cancelled"].includes(data.status)) {
          cleanupCall(false);
          toast(`Call ${data.status}.`);
          loadHistory();
        }
      } catch (e) {
        console.debug("[interpreter] call poll failed", e);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [activeCall?.id, cleanupCall, loadHistory]);

  useEffect(() => {
    if (activeCall?.status === "active" && activeCall.answered_at) {
      const start = new Date(activeCall.answered_at).getTime();
      const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
      return () => clearInterval(t);
    }
    setElapsed(0);
  }, [activeCall?.status, activeCall?.answered_at]);

  const setAvailability = async (v) => {
    setOnline(v);
    if (v && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    try {
      await api.post("/interpreter/status", { online: v });
      toast(v ? "You are now taking calls." : "You have stopped taking calls.");
    } catch { toast.error("Could not update status"); }
  };

  const acceptCall = async () => {
    if (!incoming) return;
    try {
      const { data } = await api.post(`/calls/${incoming.id}/accept`);
      setActiveCall(data);
      setIncoming(null);
      rtcRef.current = startVoiceCall({
        signaling: signalingRef.current,
        peerId: data.customer_id,
        isCaller: false,
        onRemoteStream: (stream) => {
          if (audioRef.current) { audioRef.current.srcObject = stream; audioRef.current.play().catch(() => {}); }
        },
        onStatus: (s) => setCallStatus(s),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept call");
      setIncoming(null);
    }
  };

  const declineCall = async () => {
    if (!incoming) return;
    try { await api.post(`/calls/${incoming.id}/decline`); toast("Call declined"); }
    catch (e) { console.warn("[interpreter] decline failed", e); }
    setIncoming(null);
  };

  const endCall = async () => {
    if (!activeCall) return;
    try { await api.post(`/calls/${activeCall.id}/end`); }
    catch (e) { console.warn("[interpreter] end call failed", e); }
    cleanupCall();
    loadHistory();
  };

  const toggleMute = () => {
    const next = !muted;
    rtcRef.current?.setMuted(next);
    setMuted(next);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const filteredCalls = calls.filter((c) => {
    const d = (c.started_at || "").slice(0, 10);
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Teal top bar — Propio-style */}
      <div className="bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2 font-semibold">
            <span className={`h-3 w-3 rounded-full ${online ? "bg-green-400" : "bg-slate-400"}`} data-testid="online-dot" />
            <span data-testid="welcome-text">Welcome, {user?.name} ({user?.ref_id})</span>
          </div>
          <div className="flex items-center gap-3 text-sm ml-auto">
            <span className="bg-teal-700/60 rounded-full px-3 py-1" data-testid="chip-calls">Calls: {stats.today_calls}</span>
            <span className="bg-teal-700/60 rounded-full px-3 py-1" data-testid="chip-minutes">Minutes: {stats.today_minutes.toFixed(1)}</span>
            <span className="bg-teal-700/60 rounded-full px-3 py-1" data-testid="chip-pay">Pay: ${stats.today_pay.toFixed(2)}</span>
            <button className="p-1.5 hover:bg-teal-700 rounded-md transition-colors" title="Refresh"
              data-testid="refresh-btn" onClick={loadHistory}>
              <RefreshCw className="h-4 w-4" />
            </button>
            <button className="p-1.5 hover:bg-teal-700 rounded-md transition-colors" title="Logout"
              data-testid="logout-btn" onClick={async () => { await logout(); nav("/interpreter-login"); }}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <audio ref={audioRef} autoPlay />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Call History */}
          <Card className="lg:col-span-2 border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-xl text-[#0a2540]">Call History</CardTitle>
              <span className="text-xs text-slate-400">{user?.languages?.join(", ") || "No languages assigned"}</span>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <label className="text-xs text-slate-500">Start Date</label>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    data-testid="filter-start-date" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">End Date</label>
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    data-testid="filter-end-date" className="mt-1" />
                </div>
                <Button variant="outline" size="sm" data-testid="filter-today-btn"
                  onClick={() => { setFromDate(today); setToDate(today); }}>
                  Today
                </Button>
                <Button variant="ghost" size="sm" data-testid="filter-clear-btn"
                  onClick={() => { setFromDate(""); setToDate(""); }}>
                  Clear
                </Button>
              </div>
              <Table data-testid="interpreter-calls-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Call Date</TableHead>
                    <TableHead>Call Start</TableHead>
                    <TableHead>Duration (Min)</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead>Dropped</TableHead>
                    <TableHead>Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCalls.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No calls in this range</TableCell></TableRow>
                  )}
                  {filteredCalls.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.customer_ref_id || "—"}</TableCell>
                      <TableCell>{c.customer_company || c.customer_name}</TableCell>
                      <TableCell>{new Date(c.started_at).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(c.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</TableCell>
                      <TableCell>{(c.duration_seconds / 60).toFixed(1)}</TableCell>
                      <TableCell>{c.status === "ended" ? "Yes" : "No"}</TableCell>
                      <TableCell>{["missed", "cancelled"].includes(c.status) ? "Yes" : "No"}</TableCell>
                      <TableCell>${(c.interpreter_payout || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Call Status */}
          <div className="space-y-6">
            <Card className="border-slate-200 bg-white">
              <CardContent className="pt-6">
                <h3 className="text-center font-sans text-xl font-bold text-[#0a2540]" data-testid="call-status-heading">
                  Call Status: {online ? <span className="text-teal-600">Online</span> : "Offline"}
                </h3>
                <div className="mt-6 space-y-3">
                  {!online ? (
                    <Button data-testid="start-calls-btn" onClick={() => setAvailability(true)}
                      className="w-full h-12 rounded-lg bg-[#0a2540] hover:bg-[#0a2540]/90 font-semibold">
                      Start Calls
                    </Button>
                  ) : (
                    <Button data-testid="stop-calls-btn" onClick={() => setAvailability(false)} variant="outline"
                      className="w-full h-12 rounded-lg border-rose-300 text-rose-600 hover:bg-rose-50 font-semibold">
                      Stop Calls
                    </Button>
                  )}
                </div>
                <div className="mt-6 space-y-2 text-sm border-t border-slate-100 pt-4">
                  <div className="flex justify-between"><span className="text-slate-500">Total earnings</span><span className="font-semibold" data-testid="total-payout">${billing.total_payout.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Total minutes</span><span className="font-semibold">{billing.total_minutes.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Calls taken</span><span className="font-semibold">{billing.total_calls}</span></div>
                </div>
              </CardContent>
            </Card>
            <StatementsCard testidPrefix="interpreter-statements" />
          </div>
        </div>
      </main>

      {/* Incoming call modal — caller profile */}
      {incoming && !activeCall && (
        <IncomingCallModal incoming={incoming} onAccept={acceptCall} onDecline={declineCall} />
      )}

      {/* Active call popup */}
      {activeCall && activeCall.status === "active" && (
        <ActiveCallModal
          call={activeCall}
          elapsed={fmt(elapsed)}
          callStatus={callStatus}
          muted={muted}
          onToggleMute={toggleMute}
          onEnd={endCall}
        />
      )}
    </div>
  );
}
